import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  faBorderBottomRight,
  faBorderTopLeft,
} from "@fortawesome/pro-regular-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { CornerRadius } from "../../domain/types";
import { EMAIL_DOCUMENT_MAX_BORDER_RADIUS } from "../../schema/emailDocumentContract";
import { emailBuilderCornerRadiusIsUniform } from "../../shell/panel/blockSettings/EmailBuilderBlockSettingsShared";
import { isCornerRoundingInactive } from "../settingsSectionActivation";
import { EmailBuilderSettingsFieldGrid, EmailBuilderSettingsFieldGridRow } from "./EmailBuilderSettingsFieldGrid";
import { EmailBuilderSettingsIconNumberField } from "./EmailBuilderSettingsIconNumberField";
import { EmailBuilderSettingsRadioGroup } from "./EmailBuilderSettingsRadioGroup";
import { EmailBuilderSettingsSliderField } from "./EmailBuilderSettingsSliderField";
import { useEmailBuilderSettingsPanelScrollOnChange } from "../useEmailBuilderSettingsPanelScrollOnChange";

const EMAIL_BUILDER_CORNER_KEYS = ["topLeft", "topRight", "bottomLeft", "bottomRight"] as const;

const EMAIL_BUILDER_CORNER_LABELS: Record<(typeof EMAIL_BUILDER_CORNER_KEYS)[number], string> = {
  topLeft: "Top left",
  topRight: "Top right",
  bottomLeft: "Bottom left",
  bottomRight: "Bottom right",
};

const EMAIL_BUILDER_CORNER_ICONS: Record<
  (typeof EMAIL_BUILDER_CORNER_KEYS)[number],
  { icon: IconDefinition; flipX?: boolean }
> = {
  topLeft: { icon: faBorderTopLeft },
  topRight: { icon: faBorderTopLeft, flipX: true },
  bottomRight: { icon: faBorderBottomRight },
  bottomLeft: { icon: faBorderBottomRight, flipX: true },
};

type TCornerMode = "uniform" | "custom";

function resolveCornerMode(value: CornerRadius): TCornerMode {
  return emailBuilderCornerRadiusIsUniform(value) ? "uniform" : "custom";
}

type EmailBuilderSettingsCornerRoundingSectionProps = {
  value: CornerRadius;
  onChange: (borderRadius: CornerRadius, historyGroupId?: string | null) => void;
};

export function EmailBuilderSettingsCornerRoundingSection(
  props: EmailBuilderSettingsCornerRoundingSectionProps,
) {
  const { t } = useTranslation();
  const { value, onChange } = props;
  const isUniform = emailBuilderCornerRadiusIsUniform(value);
  const [mode, setMode] = useState<TCornerMode>(() => resolveCornerMode(value));
  const wasInactiveRef = useRef(isCornerRoundingInactive(value));
  const sectionRef = useRef<HTMLDivElement | null>(null);

  useEmailBuilderSettingsPanelScrollOnChange(sectionRef, mode);

  useEffect(() => {
    const isInactive = isCornerRoundingInactive(value);
    if (wasInactiveRef.current && !isInactive) {
      setMode(resolveCornerMode(value));
    }
    wasInactiveRef.current = isInactive;
  }, [value]);

  return (
    <div ref={sectionRef} className="email-builder-settings-corner-rounding-section">
      <EmailBuilderSettingsRadioGroup
        name="email-builder-corner-mode"
        value={mode}
        options={[
          { value: "uniform", label: t("All corners") },
          { value: "custom", label: t("Customize each corner") },
        ]}
        ariaLabel={t("Corner rounding mode")}
        onChange={nextMode => {
          setMode(nextMode);
        }}
      />
      {mode === "uniform" ? (
        <EmailBuilderSettingsSliderField
          label={t("Radius (px)")}
          min={0}
          max={EMAIL_DOCUMENT_MAX_BORDER_RADIUS}
          value={value.topLeft}
          mixed={!isUniform}
          onChange={(next, historyGroupId) => {
            onChange(
              {
                topLeft: next,
                topRight: next,
                bottomRight: next,
                bottomLeft: next,
              },
              historyGroupId,
            );
          }}
        />
      ) : (
        <EmailBuilderSettingsFieldGrid>
          <EmailBuilderSettingsFieldGridRow>
            {(["topLeft", "topRight"] as const).map(corner => {
              const cornerIcon = EMAIL_BUILDER_CORNER_ICONS[corner];
              return (
                <EmailBuilderSettingsIconNumberField
                  key={corner}
                  label={t("{{corner}} (px)", { corner: t(EMAIL_BUILDER_CORNER_LABELS[corner]) })}
                  icon={cornerIcon.icon}
                  iconFlipX={cornerIcon.flipX}
                  value={value[corner]}
                  min={0}
                  max={EMAIL_DOCUMENT_MAX_BORDER_RADIUS}
                  onChange={(next, historyGroupId) => {
                    onChange({ ...value, [corner]: next }, historyGroupId);
                  }}
                />
              );
            })}
          </EmailBuilderSettingsFieldGridRow>
          <EmailBuilderSettingsFieldGridRow>
            {(["bottomLeft", "bottomRight"] as const).map(corner => {
              const cornerIcon = EMAIL_BUILDER_CORNER_ICONS[corner];
              return (
                <EmailBuilderSettingsIconNumberField
                  key={corner}
                  label={t("{{corner}} (px)", { corner: t(EMAIL_BUILDER_CORNER_LABELS[corner]) })}
                  icon={cornerIcon.icon}
                  iconFlipX={cornerIcon.flipX}
                  value={value[corner]}
                  min={0}
                  max={EMAIL_DOCUMENT_MAX_BORDER_RADIUS}
                  onChange={(next, historyGroupId) => {
                    onChange({ ...value, [corner]: next }, historyGroupId);
                  }}
                />
              );
            })}
          </EmailBuilderSettingsFieldGridRow>
        </EmailBuilderSettingsFieldGrid>
      )}
    </div>
  );
}
